import React, { useEffect, useState } from 'react';
import { getTrends, getDocumentTypes } from '../api/client';

const TYPE_LABEL = {
  standard_apartment: 'Apartment (VvE)',
  house:              'House',
  erfpacht_apartment: 'Erfpacht Apartment',
  erfpacht_house:     'Erfpacht House',
};

/* Status colours are the platform's own (global.css) and are used for risk only —
   bars, dots and tick squares carry them; all text stays in text tokens. Amber on
   white is below 3:1 contrast, so every proportion also shows its counts as text. */
const RED = 'var(--red)';
const AMBER = 'var(--amber)';

/* Oversized numeral — same Hanken Grotesk stack the platform's .numeral uses. */
const NUMERAL_FONT = "'Hanken Grotesk', 'DM Sans', sans-serif";

function Dot({ color }) {
  return <span style={{
    display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
    background: color, marginRight: 6, flexShrink: 0,
  }} />;
}

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 18, fontSize: 12.5, color: 'var(--text-label)', marginBottom: 16 }}>
      <span><Dot color={RED} />Red</span>
      <span><Dot color={AMBER} />Amber</span>
    </div>
  );
}

function BigNumber({ value, label, color }) {
  return (
    <div>
      <div style={{
        fontFamily: NUMERAL_FONT, fontVariantNumeric: 'tabular-nums',
        fontSize: 'clamp(2.6rem, 4vw, 3.4rem)', fontWeight: 500,
        lineHeight: 1, letterSpacing: '-0.04em', color: color || 'var(--text)',
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 11, fontWeight: 500, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: 'var(--text-label)', marginTop: 8,
      }}>
        {label}
      </div>
    </div>
  );
}

/* "4 of 6" as 6 tick squares with 4 filled. Fill carries the risk colour;
   the count is also written out so colour is never the only channel. */
function TickSquares({ filled, total, color }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ display: 'inline-flex', gap: 3 }} aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
          <span key={i} style={{
            width: 11, height: 11, borderRadius: 2.5,
            background: i < filled ? (color || RED) : 'transparent',
            border: i < filled ? 'none' : '1px solid rgba(19,19,19,0.18)',
          }} />
        ))}
      </span>
      <span style={{
        fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {filled} of {total}
      </span>
    </span>
  );
}

/* Horizontal stacked bar: red then amber, square at the baseline, 4px rounded
   data-end, 2px card-surface gap between the two segments. */
function FlagBar({ red, amber, max, height = 18 }) {
  const pct = (n) => `${(n / max) * 100}%`;
  return (
    <div style={{ display: 'flex', flex: 1, height, alignItems: 'stretch' }}>
      {red > 0 && (
        <div title={`${red} red`} style={{
          width: pct(red), background: RED, minWidth: 6,
          borderRadius: amber > 0 ? 0 : '0 4px 4px 0',
        }} />
      )}
      {red > 0 && amber > 0 && <div style={{ width: 2, flexShrink: 0 }} />}
      {amber > 0 && (
        <div title={`${amber} amber`} style={{
          width: pct(amber), background: AMBER, minWidth: 6,
          borderRadius: '0 4px 4px 0',
        }} />
      )}
    </div>
  );
}

function CountText({ red, amber }) {
  return (
    <span style={{
      fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums',
    }}>
      {red > 0 && <span>{red} red</span>}
      {red > 0 && amber > 0 && <span> · </span>}
      {amber > 0 && <span>{amber} amber</span>}
    </span>
  );
}

function AgentAvatar({ name }) {
  return (
    <span title={name} style={{
      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
      background: 'var(--ink)', color: 'var(--cream)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12.5, fontWeight: 600,
    }}>
      {name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

export default function Trends() {
  const [data, setData] = useState(null);
  const [docLabels, setDocLabels] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getTrends(), getDocumentTypes()])
      .then(([trends, types]) => {
        setData(trends);
        const map = {};
        (types || []).forEach(t => { map[t.id] = t.label; });
        setDocLabels(map);
      })
      .finally(() => setLoading(false));
  }, []);

  // Synthesis findings carry composite types like "mjop+vve_jaarrekening".
  const docLabel = (dt) => (dt || '')
    .split('+')
    .map(part => docLabels[part.trim()] || part.trim())
    .join(' + ');

  if (loading) {
    return (
      <div className="page theme-cream">
        <p className="text-muted" style={{ padding: '80px 0', textAlign: 'center' }}>Loading trends…</p>
      </div>
    );
  }

  if (!data || !data.reviews_analysed) {
    return (
      <div className="page theme-cream">
        <div className="page-header" style={{ marginBottom: 40 }}>
          <div className="eyebrow" style={{ marginBottom: 16 }}>Portfolio Insights</div>
          <h1 className="display-lg">Trends</h1>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '56px 32px' }}>
          <h3 style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>No analysed reviews yet</h3>
          <p className="text-muted text-sm">Trends appear here once at least one analysis has completed.</p>
        </div>
      </div>
    );
  }

  const { reviews_analysed: n, doc_types, property_types, recurring_findings,
          corrections, missing_docs, averages, agents = [] } = data;
  const maxFlags = Math.max(...doc_types.map(d => d.red + d.amber), 1);
  const overrideRate = corrections.total_visible_findings
    ? Math.round((corrections.manual_overrides / corrections.total_visible_findings) * 1000) / 10
    : 0;

  return (
    <div className="page theme-cream">
      <div className="page-header" style={{ marginBottom: 40 }}>
        <div className="eyebrow" style={{ marginBottom: 16 }}>Portfolio Insights</div>
        <h1 className="display-lg" style={{ marginBottom: 14 }}>Trends</h1>
        <p className="text-muted">
          What keeps coming up across all {n} analysed package{n !== 1 ? 's' : ''} — input for
          sharpening the checklist. Counts use the final flag colour, including manual and
          automatic corrections.
          {n < 10 && ' Small sample so far; patterns firm up as more packages are analysed.'}
        </p>
      </div>

      {/* Stat tiles */}
      <div className="numerals-row" style={{ marginBottom: 44 }}>
        {[
          { cls: '',      value: n,                          label: 'Reviews analysed' },
          { cls: 'red',   value: averages.red_per_review,    label: 'Avg red / review' },
          { cls: 'amber', value: averages.amber_per_review,  label: 'Avg amber / review' },
          { cls: 'green', value: averages.green_per_review,  label: 'Avg clear / review' },
          ...(averages.cost_per_review_usd
            ? [{ cls: '', value: `$${averages.cost_per_review_usd}`, label: 'Avg cost / review' }]
            : []),
        ].map((s, i) => (
          <div className={`numeral ${s.cls}`} key={i}>
            <div className="value">{s.value}</div>
            <div className="label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: '26px 28px', marginBottom: 48 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
          Where the flags are
        </h3>
        <p style={{ fontSize: 12.5, color: 'var(--text-label)', marginBottom: 18 }}>
          Document types ranked by red + amber findings across all reviews.
        </p>
        <Legend />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {doc_types.map(d => (
            <div key={d.doc_type} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{
                width: 220, flexShrink: 0, fontSize: 13.5, color: 'var(--text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }} title={docLabel(d.doc_type)}>
                {docLabel(d.doc_type)}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                <FlagBar red={d.red} amber={d.amber} max={maxFlags} />
                <CountText red={d.red} amber={d.amber} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section intro — personal, first person */}
      <div style={{ margin: '0 0 28px', maxWidth: 640 }}>
        <h2 style={{
          fontSize: 'clamp(1.5rem, 2.6vw, 2rem)', fontWeight: 600,
          letterSpacing: '-0.02em', color: 'var(--text)', marginBottom: 10,
        }}>
          What the last {n} reviews taught us
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {agents.length > 0 && (
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {agents.map(a => <AgentAvatar key={a.name} name={a.name} />)}
            </span>
          )}
          <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0 }}>
            {agents.length > 0
              ? `Built from the packages ${agents.map(a => a.name.split(' ')[0]).join(' and ')} ran through the platform — and from every correction made along the way.`
              : 'Built from every package run through the platform — and from every correction made along the way.'}
          </p>
        </div>
      </div>

      {/* Property-type cards — lead with the number, bars instead of ordinals */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 20, marginBottom: 20, alignItems: 'stretch',
      }}>
        {property_types.map(pt => {
          const cardMax = Math.max(...pt.top_doc_types.map(d => d.red + d.amber), 1);
          return (
            <div className="card" key={pt.property_type} style={{
              padding: '26px 28px', marginBottom: 0,
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <BigNumber
                  value={pt.red + pt.amber}
                  label={TYPE_LABEL[pt.property_type] || pt.property_type}
                />
                <span style={{ fontSize: 12.5, color: 'var(--text-label)', whiteSpace: 'nowrap', paddingTop: 6 }}>
                  {pt.reviews} review{pt.reviews !== 1 ? 's' : ''}
                </span>
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '10px 0 18px' }}>
                {pt.red} red and {pt.amber} amber findings across {pt.reviews} package{pt.reviews !== 1 ? 's' : ''}.
              </p>
              {pt.top_doc_types.length === 0 ? (
                <p className="text-muted text-sm">No red or amber findings.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 'auto' }}>
                  {pt.top_doc_types.map(d => (
                    <div key={d.doc_type}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
                        <span style={{
                          fontSize: 13, color: 'var(--text)', minWidth: 0,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={docLabel(d.doc_type)}>
                          {docLabel(d.doc_type)}
                        </span>
                        <CountText red={d.red} amber={d.amber} />
                      </div>
                      <FlagBar red={d.red} amber={d.amber} max={cardMax} height={10} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recurring findings — full-width butter "pinned note" centrepiece */}
      <div style={{
        background: 'var(--cream)', borderRadius: 16,
        boxShadow: '0 4px 16px rgba(80,66,30,0.10)',
        padding: 'clamp(30px, 4vw, 48px)', marginBottom: 48,
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: 'var(--text-label)', marginBottom: 10,
        }}>
          Pinned for the checklist
        </div>
        <h2 style={{
          fontSize: 'clamp(1.3rem, 2.2vw, 1.7rem)', fontWeight: 600,
          letterSpacing: '-0.02em', color: 'var(--text)', marginBottom: 8,
        }}>
          The findings that keep coming back
        </h2>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', maxWidth: 520, margin: '0 auto 26px' }}>
          When the same red or amber finding shows up review after review, it has earned a
          permanent place in the fixed checklist.
        </p>
        {recurring_findings.length === 0 ? (
          <p className="text-muted text-sm">Nothing has recurred across reviews yet — every red and amber so far has been package-specific.</p>
        ) : (
          <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'left' }}>
            {recurring_findings.map((f, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                padding: '14px 0',
                borderTop: i > 0 ? '1px solid rgba(19,19,19,0.08)' : 'none',
              }}>
                <Dot color={f.color === 'red' ? RED : AMBER} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{f.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-label)', marginTop: 2 }}>
                    {docLabel(f.doc_type)}
                  </div>
                </div>
                <TickSquares filled={f.reviews} total={n} color={f.color === 'red' ? RED : AMBER} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Missing docs + corrections — number-led, equal heights */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 20, alignItems: 'stretch',
      }}>
        <div className="card" style={{ padding: '26px 28px', marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
          <BigNumber value={missing_docs.length} label="Documents that go missing" />
          <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '10px 0 18px' }}>
            Required documents the completeness check has found absent, and in how many of
            the {n} packages.
          </p>
          {missing_docs.length === 0 ? (
            <p className="text-muted text-sm">No required documents have been missing.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 'auto' }}>
              {missing_docs.map(m => (
                <div key={m.doc_type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <span style={{
                    fontSize: 13, color: 'var(--text)', minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }} title={m.doc_label || docLabel(m.doc_type)}>
                    {m.doc_label || docLabel(m.doc_type)}
                  </span>
                  <TickSquares filled={m.reviews} total={n} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: '26px 28px', marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
          <BigNumber value={`${overrideRate}%`} label="Where you've had to correct us" />
          <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '10px 0 18px' }}>
            {corrections.manual_overrides} of the {corrections.total_visible_findings} findings
            we've shown you were manually re-coloured. Every one of those teaches us where the
            analysis is systematically off.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, marginTop: 'auto' }}>
            {[
              ['Manual colour overrides', corrections.manual_overrides],
              ['Automatic tier adjustments', corrections.auto_adjustments],
              ['Severity-audit escalations', corrections.severity_escalations],
              ['Findings reviewed in total', corrections.total_visible_findings],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: 'var(--text)' }}>{label}</span>
                <span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
